import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import classnames from 'classnames/bind';
import { Row, Col, Icon } from 'grab-ui';
import ContextualOnboarding from 'components/v2/contextual-onboarding';
import InputFieldV2 from 'components/v2/input-field/input-field';
import NameField from 'components/booking/name-field/name-field';
import PhoneField from 'components/booking/phone-field/phone-field';
import InputField from 'components/form-field/input-field/input-field';
import DivButton from 'components/v2/div-button/div-button';
import ShopifyDetailsPopover from 'views/v2/shopify-home/shopify-details-popover/shopify-details-popover';
import { COUNTRY_MAP } from 'constants/country-constants';
import { VALIDATE_MESSAGE } from 'constants/booking-constants';
import formatCurrency from 'common/utils/format-currency';
import ShopifyOrderDetailModal from 'components/v2/shopify-order-detail-modal';
import {
  SCRIBE_STATE_NAMES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import OrderItemOnboarding from 'views/v2/shopify-home/common/order-item-onboarding';

import css from './customer-info.less';
import NumberedPin from '../common/numbered-pin';

const cx = classnames.bind(css);

class CustomerInfo extends Component {
  static contextType = ShopifyTrackEventContext;
  constructor(props) {
    super(props);
    const { idSuffix } = props;
    this.state = {
      showInputFields: true,
      showLocationNotesInput: false,
    };
    this.fieldNames = {
      name: `name-${idSuffix}`,
      phone: `phone-${idSuffix}`,
      cod: `cod-${idSuffix}`,
      note: `note-${idSuffix}`,
    };
    this.initialed = false;
  }

  componentDidMount() {
    this.initFormOnce();
  }

  componentDidUpdate(prevProps) {
    this.initFormOnce();
  }

  initFormOnce = () => {
    if (this.props.isViewMode || this.initialed) {
      return;
    }

    this.initialed = true;
    const { info, form: { setFieldsValue } } = this.props;
    // initial field values
    // dont compare with prevProps because recipient prevProps always available, current flow only render recipient if existed
    const fields = {};
    Object.entries(this.fieldNames).forEach(([key, fieldName]) => {
      if (!_isEmpty(info[key])) {
        fields[fieldName] = info[key];
      }
    });
    setFieldsValue(fields);
  };

  getBackgroundImageClass = () => {
    const { isSender, noOfRecipients } = this.props;
    if (isSender) return 'pickupBackground';
    if (noOfRecipients > 1) return 'dropoffNumberBackground';
    return 'dropoffNoNumberBackground';
  };

  getDisplayNumber = () => {
    const { noOfRecipients, index } = this.props;
    if (noOfRecipients > 1) return index + 1;
    return 0;
  };

  toggleLocationNotes = show => () => {
    this.setState({
      showLocationNotesInput: show,
    });
  };

  validateCODAmount = (rule, value, callback) => {
    const { countryCode, minCODAmount, maxCODAmount } = this.props;
    const currency = _get(COUNTRY_MAP[countryCode], 'currency', {});
    // skip validate when COD amount is not a number
    if (!value) {
      callback();
      return;
    }
    // validate negative COD amount
    if (value <= 0) {
      callback(I18n.t(VALIDATE_MESSAGE.COD_INVALID_AMOUNT));
      return;
    }
    if (minCODAmount != null && value < minCODAmount) {
      callback(
        I18n.t(VALIDATE_MESSAGE.COD_MIN_AMOUNT, {
          minAmount: formatCurrency({
            code: currency.code,
            symbol: currency.symbol,
            amount: minCODAmount,
            hideSymbol: false,
          }),
        }),
      );
      return;
    }
    if (maxCODAmount != null && value > maxCODAmount) {
      callback(
        I18n.t(VALIDATE_MESSAGE.COD_MAX_AMOUNT, {
          maxAmount: formatCurrency({
            code: currency.code,
            symbol: currency.symbol,
            amount: maxCODAmount,
            hideSymbol: false,
          }),
        }),
      );
      return;
    }
    callback();
  };

  showShopifyOrderDetailModal = (shopifyMetaData, contact) => {
    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.SHOPIFY_ORDER_NUMBER,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: _get(
          shopifyMetaData,
          'ID',
          '',
        ),
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_ITEMS]: _get(
          shopifyMetaData,
          'lineItems.length',
          0,
        ),
      },
    });
    return ShopifyOrderDetailModal({
      ctx: this.context,
      shopifyMetaData,
      contact,
    });
  };

  onHoverShopifyDetail = () => {
    const { shopifyFulfillmentOrder } = this.props;
    this.context.fireShopifyTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_SHOPIFY_EVENTS.SHOPIFY_ITEM_DETAIL,
      facets: {
        [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: _get(
          shopifyFulfillmentOrder,
          'ID',
        ),
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_ITEMS]: _get(shopifyFulfillmentOrder, [
          'lineItems',
          'length',
        ]),
        [SCRIBE_SHOPIFY_PARAMS.TOTAL_PRICE]: _get(
          shopifyFulfillmentOrder,
          'subtotalPrice',
        ),
        [SCRIBE_SHOPIFY_PARAMS.CUSTOMER_NOTE]: _get(
          shopifyFulfillmentOrder,
          'note',
        ),
      },
    });
  };

  renderInputFields = () => {
    const { showInputFields } = this.state;
    const { renderCOD } = this.props;

    if (!showInputFields) return null;

    const { isSender, countryCode, form } = this.props;
    const currencyCode = _get(COUNTRY_MAP[countryCode], 'currency.code', '');

    return (
      <Row type="flex" gutter={{ md: 16 }} className={css.inputFields}>
        <Col xs={24} md={8}>
          <NameField
            id={this.fieldNames.name}
            label=""
            placeholder={
              isSender
                ? I18n.t('booking.fieldLabel.senderName')
                : I18n.t('booking.fieldLabel.recipientName')
            }
            // Used to differentiate validation message for sender/ receiver
            isSender={isSender}
            form={form}
            isRequired
          />
        </Col>
        <Col xs={24} md={8}>
          <PhoneField
            id={this.fieldNames.phone}
            label=""
            placeholder={I18n.t('v2.homepage.manualOrder.placeholder.phone')}
            form={form}
            // Used to differentiate validation message for sender/ receiver
            isSender={isSender}
          />
        </Col>
        <Col xs={24} md={8} className={css.amountField}>
          {!isSender &&
            renderCOD && (
              <InputFieldV2
                id={this.fieldNames.cod}
                type="number"
                placeholder={I18n.t('v2.homepage.manualOrder.placeholder.cod')}
                prefix={currencyCode}
                fieldOptions={{
                  rules: [
                    {
                      validator: this.validateCODAmount,
                    },
                  ],
                }}
                form={form}
              />
            )}
        </Col>
      </Row>
    );
  };

  renderLocationNotes = () => {
    const { form } = this.props;
    const { showLocationNotesInput } = this.state;

    return (
      <Row
        className={cx('locationNotes', {
          'locationNotes-focus': showLocationNotesInput,
        })}
        gutter={{ md: 16 }}
      >
        <Col md={16} xs={24} onClick={this.toggleLocationNotes(true)}>
          <InputField
            id={this.fieldNames.note}
            size="small"
            label=""
            placeholder={I18n.t(
              'v2.homepage.manualOrder.placeholder.locationNote',
            )}
            onBlur={this.toggleLocationNotes(false)}
            form={form}
          />
        </Col>
      </Row>
    );
  };

  renderCustomerInfo = () => {
    const {
      info,
      onClickLocation,
      isSender,
      onDelete,
      disableDeletion,
    } = this.props;
    const { keywords } = info;

    if (_isEmpty(keywords)) {
      return (
        <DivButton className={css.emptyLocation} onClick={onClickLocation}>
          {I18n.t(
            `v2.homepage.manualOrder.placeholder.${
              isSender ? 'senderLocation' : 'recipientLocation'
            }`,
          )}
        </DivButton>
      );
    }

    /* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
    return (
      <div>
        <div className={css.locationContainer}>
          <h5
            onClick={onClickLocation}
            role="button"
            tabIndex="0"
            onKeyDown={null}
          >
            {keywords}
          </h5>
          {!isSender &&
            !disableDeletion && (
              <DivButton className={css.closeIcon} onClick={onDelete}>
                <Icon type="grabicon-close-01" />
              </DivButton>
            )}
        </div>
        {this.renderLocationNotes()}
        {this.renderInputFields()}
      </div>
    );
    /* eslint-enable */
  };

  renderShopifyOrderLink = (index, shopifyMetaData, info) => (
    <a
      role="button"
      onClick={() => this.showShopifyOrderDetailModal(shopifyMetaData, info)}
      tabIndex={0}
      onKeyDown={null}
    >
      {!index ? I18n.t('shopify.orders.customerInfo') : ','}{' '}
      {_get(shopifyMetaData, 'name', '-')}
    </a>
  );

  renderMultipleShopifyOrder = (shopifyMetaDatas, info) => (
    <h6>
      {shopifyMetaDatas.map((shopifyMetaData, index) =>
        this.renderShopifyOrderLink(index, shopifyMetaData, info),
      )}
    </h6>
  );

  renderCustomerInfoView = () => {
    const {
      info,
      isSender,
      shopifyFulfillmentOrder,
      isShopifyOrder,
      shopifyMetaDatas,
      isContextualOnboardingEnabled,
    } = this.props;
    const { keywords } = info;
    const showShopifyDetails = !_isEmpty(shopifyFulfillmentOrder);

    if (_isEmpty(keywords)) {
      return (
        <div className={css.emptyLocation}>
          {I18n.t(
            `v2.homepage.manualOrder.placeholder.${
              isSender ? 'addPickupLocation' : 'addDropOffLocation'
            }`,
          )}
        </div>
      );
    }

    // eslint-disable-next-line prettier/prettier
    const shopifyDetailsElementID = `bookingFormShopifyOrderDetail-${
      shopifyFulfillmentOrder.ID
    }`;

    const shopifyDetailsElement = showShopifyDetails ? (
      <h6>
        <ShopifyDetailsPopover fulfillmentOrder={shopifyFulfillmentOrder}>
          <span
            id={shopifyDetailsElementID}
            onMouseEnter={this.onHoverShopifyDetail}
          >
            {I18n.t('shopify.orders.customerInfo')}{' '}
            {shopifyFulfillmentOrder.orderName}
          </span>
        </ShopifyDetailsPopover>
        {isContextualOnboardingEnabled && (
          <ContextualOnboarding
            elementID={shopifyDetailsElementID}
            onboardingID={'shopifyBookingFormOrderItemPrice'}
            onboardingVisual={<OrderItemOnboarding />}
            onboardingVisualPlacement={'bottom'}
            maxOnboardingTimes={1}
            delay={500}
            highlightPaddingVertical={2}
            highlightPaddingHorizontal={6}
          />
        )}
      </h6>
    ) : null;

    const { name, phone } = info;
    return (
      <Fragment>
        <h5>
          {name} • {phone}
        </h5>
        <h6>{keywords}</h6>
        {shopifyDetailsElement}
        {!isSender &&
          isShopifyOrder &&
          this.renderMultipleShopifyOrder(shopifyMetaDatas, info)}
      </Fragment>
    );
  };

  render() {
    const { isViewMode, noOfRecipients, index, isSender } = this.props;
    const isLastCustomerInfo = index + 1 === noOfRecipients;
    const displayNumber = this.getDisplayNumber();

    return (
      <div className={css.customerInfo}>
        <div className={css.iconContainer}>
          <NumberedPin
            className={css.numberedPinOverwrite}
            showNumber={displayNumber > 0}
            number={displayNumber}
            isSender={isSender}
          />
          <div className={cx({ dottedLine: !isLastCustomerInfo })} />
        </div>
        <div className={cx('info', { bottomPadding: !isLastCustomerInfo })}>
          {isViewMode
            ? this.renderCustomerInfoView()
            : this.renderCustomerInfo()}
        </div>
      </div>
    );
  }
}

CustomerInfo.propTypes = {
  isViewMode: PropTypes.bool,
  info: PropTypes.shape({}),
  idSuffix: PropTypes.string, // use as a suffix of phone, name, COD id fields
  isSender: PropTypes.bool.isRequired,
  countryCode: PropTypes.string,
  onClickLocation: PropTypes.func,
  onDelete: PropTypes.func,
  form: PropTypes.shape({}),
  disableDeletion: PropTypes.bool,
  index: PropTypes.number,
  noOfRecipients: PropTypes.number,
  renderCOD: PropTypes.bool,
  minCODAmount: PropTypes.number,
  maxCODAmount: PropTypes.number,
  shopifyFulfillmentOrder: PropTypes.shape({}),
  isShopifyOrder: PropTypes.bool,
  shopifyMetaDatas: PropTypes.arrayOf(PropTypes.shape({})),
  isContextualOnboardingEnabled: PropTypes.bool,
};

CustomerInfo.defaultProps = {
  isViewMode: false,
  info: {},
  idSuffix: '',
  onClickLocation: () => {},
  form: {},
  onDelete: undefined,
  disableDeletion: false,
  index: 0,
  noOfRecipients: 0,
  countryCode: '',
  renderCOD: false,
  minCODAmount: 0,
  maxCODAmount: 0,
  shopifyFulfillmentOrder: {},
  isShopifyOrder: false,
  shopifyMetaDatas: [],
  isContextualOnboardingEnabled: false,
};

export default CustomerInfo;
